import { useMemo } from 'react';
import { createListCollection, Select as ChakraSelect } from '@chakra-ui/react';
import * as React from 'react';
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from '@/components/ui/select';

export interface SimpleSelectItem {
  label: string;
  value: string;
}

interface SimpleSelectProps
  extends Omit<ChakraSelect.RootProps, 'collection' | 'value' | 'onValueChange'> {
  items: SimpleSelectItem[];
  /** Selected value; '' (or undefined) shows the placeholder. */
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  /**
   * When rendered inside a Dialog, pass the DialogContent ref so the menu
   * portals into the dialog — above its z-index and inside its focus trap.
   */
  contentRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Single-value DS-styled dropdown over {label, value} items — the common case
 * that native <select> used to cover, but with the design-system menu surface
 * instead of the OS-drawn list. For multi-select or custom item rendering,
 * compose src/components/ui/select.tsx directly.
 */
export function SimpleSelect({
  items,
  value,
  onValueChange,
  placeholder,
  contentRef,
  ...rest
}: SimpleSelectProps) {
  const collection = useMemo(() => createListCollection({ items }), [items]);
  return (
    <SelectRoot
      collection={collection}
      value={value ? [value] : []}
      onValueChange={({ value: next }) => onValueChange?.(next[0] ?? '')}
      {...rest}
    >
      <SelectTrigger>
        <SelectValueText placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent portalRef={contentRef as React.RefObject<HTMLElement> | undefined}>
        {items.map((item) => (
          <SelectItem key={item.value} item={item}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </SelectRoot>
  );
}
